/**
 * PageContainer component - Main page layout container
 *
 * Provides the primary layout structure for pages, handling
 * responsive constraints, spacing, and accessibility features.
 */

import { Box, SxProps } from '@mui/material';
import { ReactNode, forwardRef } from 'react';

export interface PageContainerProps {
  /** Page content */
  children: ReactNode;
  /** Whether to include top padding (useful when header is fixed) */
  includeTopPadding?: boolean;
  /** Whether to include bottom padding */
  includeBottomPadding?: boolean;
  /** Minimum height behavior */
  minHeight?: 'viewport' | 'content' | 'none';
  /** Custom sx props for styling */
  sx?: SxProps;
  /** HTML element type */
  component?: React.ElementType;
  /** Additional props passed to the root element */
  [key: string]: unknown;
}

/**
 * Main page container component
 */
export const PageContainer = forwardRef<HTMLElement, PageContainerProps>(
  (
    {
      children,
      includeTopPadding = false,
      includeBottomPadding = true,
      minHeight = 'viewport',
      sx,
      component = 'main',
      ...props
    },
    ref,
  ) => {
    // Minimum height variants
    const minHeightVariants = {
      viewport: {
        minHeight: '100vh',
      },
      content: {
        minHeight: 'max-content',
      },
      none: {},
    };

    return (
      <Box
        component={component as React.ElementType}
        ref={ref}
        sx={[
          {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            backgroundColor: 'background.default',
            color: 'text.primary',
            ...minHeightVariants[minHeight as keyof typeof minHeightVariants],
            pt: includeTopPadding ? { xs: 4, md: 5 } : 0,
            pb: includeBottomPadding ? { xs: 4, md: 5 } : 0,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...props}
      >
        {children as React.ReactNode}
      </Box>
    );
  },
);

PageContainer.displayName = 'PageContainer';

export default PageContainer;
